import React from "react";
import { DurationUnit } from "luxon";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { MultipleComponentPopup } from "@/Common/Components/Modals/MultipleComponentPopup";
import { DateTime } from "@/Common/Helpers/DateTime";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CreatinineClearanceAlgorithmLookup, TemperatureLocationsLookup } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BloodGlucoseConcentrationUnit, HeightUnit, SerumCreatinineConcentrationUnit, TemperatureUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { CurrentEmployeeHeader } from "@/Patient/Components/VitalSigns/CurrentEmployeeHeader";
import { PainScaleComponent } from "@/Patient/Components/VitalSigns/PainScaleComponent";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { VitalSignPercentileUtility } from "@/Patient/Helpers/VitalSignPercentileUtility";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { GrowthChartsCacheData } from "@/Patient/Models/GrowthCharts/GrowthChartsCacheData";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { HeartRateRythm, VitalSign } from "@/Patient/Models/VitalSigns/VitalSign";
import { VitalSignModelConverter } from "@/Patient/Models/VitalSigns/VitalSignModelConverter";
import { BloodPressureField } from "../MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { VitalSignActions } from "./VitalSignActions";

import "./VitalSignPopup.css";

interface VitalSignPopupProps {
    onClose: (encounterId?: string, vitalSign?: MedicalItemVitalSign) => void;
    patientData?: VitalSignPatientData;
    isAddingToExistingEncounter: boolean;
    growthChartsCache?: GrowthChartsCacheData;
    vitalSignToEdit?: MedicalItemVitalSign;
    vitalSignToEditReaon?: string;
    disabledSavePlus?: boolean;
}

interface VitalSignDefaultUnits {
    weightUnit: WeightUnit;
    temperatureUnit: TemperatureUnit;
    heightUnit: HeightUnit;
    headCircumferenceUnits: HeightUnit;
    waistCircumferenceUnits: HeightUnit;
    serumCreatinineConcentrationUnit: SerumCreatinineConcentrationUnit;
    bloodGlucoseUnits: BloodGlucoseConcentrationUnit;
}

class VitalSignPopupState {
    public vitalSign: VitalSign = new VitalSign();
    public showExtraFields: boolean = false;
    public reason: string = "";
    public showReasonClickableText: boolean = false;
    public currentSelectedSpecialty: string = "";
    public percentiles: { [index in VitalProperty]?: string };
    public vitalSignToEdit?: MedicalItemVitalSign;
    public saveLock: boolean;

    public constructor(fields?: Partial<VitalSignPopupState>) {
        Object.assign(this, fields);
    }
}

export class VitalSignPopup extends BaseReactComponent<VitalSignPopupProps, VitalSignPopupState> {
    private percentileUtility: VitalSignPercentileUtility | null = null;
    public constructor(props: VitalSignPopupProps) {
        super(props);
        if (props.growthChartsCache) {
            this.percentileUtility = new VitalSignPercentileUtility(props.growthChartsCache, props.patientData);
        }
        this.state = new VitalSignPopupState({
            vitalSign: new VitalSign({ ...this.getDefaultUnits(), date: DateTime.nowDate() }),
            currentSelectedSpecialty: sessionStore.getCurrentEmployee()
                .getServiceSpecializations()
                .firstOrDefault()
                .LookupID,
            reason: this.getReason(true),
            percentiles: this.percentileUtility?.getAllCalculatedPercentiles(new VitalSign()) ?? {}
        });
    }

    public componentDidMount(): void {
        this.setOverlay("growth-charts-vital-sign-popup-overlay");
        this.mutateState(s => {
            if (this.props.vitalSignToEdit) {
                s.vitalSign = VitalSignModelConverter.convertToVitalSign(this.props.vitalSignToEdit);
                s.vitalSignToEdit = this.props.vitalSignToEdit;
                s.reason = this.getReason(false);
                s.saveLock = true; // Lock save when editing to prevent quick save on unchanged fields
            }

            s.percentiles = this.percentileUtility?.getAllCalculatedPercentiles(s.vitalSign) ?? {};
        });
    }

    public render(): JSX.Element {
        const vitalSign = this.state.vitalSign;
        const vitalSignReasonTextBoxId = "vital-sign-reason";

        return (
            <MultipleComponentPopup
                shouldCloseOnClickOutside={false}
                onCloseCallback={() => this.props.onClose()}
                contentJustification="center"
                contentMarginTop={-30}
            >
                <div className="popup vital-sign-popup" style={{ position: "unset" }}>
                    <div className="p-header">
                        <a className="close" aria-label="Close" onClick={() => this.props.onClose()}> x</a>
                        <h4>{this.getTitle()}</h4>
                    </div>
                    <div>
                        <div className="vital-sign-content">
                            <div className="padding-md">
                                <CurrentEmployeeHeader
                                    currentEmployee={sessionStore.getCurrentEmployee()}
                                    currentSpecialtyId={this.state.currentSelectedSpecialty}
                                    onToggleSpecialization={specialtyId => this.mutateState(s => {
                                        s.currentSelectedSpecialty = specialtyId;
                                        s.saveLock = false;
                                    })}
                                />
                                {this.renderRow(
                                    "ReasonLabel",
                                    <div>
                                        <TextboxControl
                                            id={vitalSignReasonTextBoxId}
                                            value={this.state.reason}
                                            onChange={text => this.mutateState(s => {
                                                s.reason = text;
                                                s.saveLock = false;
                                            })}
                                        />
                                        <FixedClickableTextPanel
                                            cssClass="text-helper-container"
                                            context="RFV"
                                            inputElementId={vitalSignReasonTextBoxId}
                                            isClickableTextPanelOpen={this.state.showReasonClickableText}
                                            isLinkDisabled={false}
                                            doNotAdjustPosition
                                            onClickableTextPanelOpen={() => this.mutateState(s => s.showReasonClickableText = true)}
                                            onClickableTextPanelClose={() => this.mutateState(s => s.showReasonClickableText = false)}
                                            isAboveLabel
                                            hasResponsiveHeight
                                        />
                                    </div>
                                )}
                                {this.renderRow(
                                    "DateLabel",
                                    <DateSelector
                                        date={this.state.vitalSign.date}
                                        id="growcharts-date"
                                        dataCy="growthCharts-vitalSign-date"
                                        valid={!!this.state.vitalSign.date}
                                        dateFormat="yyyy-MM-dd HH:mm:ss"
                                        onChangeCallback={value => this.mutateState(s => {
                                            s.vitalSign.date = value;
                                            s.percentiles = this.percentileUtility?.getAllCalculatedPercentiles(s.vitalSign);
                                            s.saveLock = false;
                                        })}
                                        disabled={this.state.vitalSign.isCommited}
                                        toDayEnabled
                                    />
                                )}
                                {this.renderRow("WeightLabel", this.renderWeight())}
                                {this.renderRow(
                                    "HeightLabel",
                                    this.renderDistanceField(
                                        "vital-sign-height",
                                        vitalSign.height,
                                        value => this.mutateState(s => {
                                            s.vitalSign.height = value;
                                            if (this.percentileUtility) {
                                                s.percentiles["Height"] = this.percentileUtility.calculatePercentile("Height", s.vitalSign);
                                                s.percentiles["MassBodyIndex"] = this.percentileUtility.calculatePercentile("MassBodyIndex", s.vitalSign);
                                            }
                                            s.saveLock = false;
                                        }),
                                        vitalSign.heightUnit,
                                        value => this.mutateState(s => {
                                            s.vitalSign.heightUnit = value;
                                            s.saveLock = false;
                                        }),
                                        this.renderPercentile("Height")
                                    )
                                )}
                                {this.renderRow(
                                    "BMILabel",
                                    <div className="flex-inline-spacing">
                                        <TextboxControl
                                            className="form-control text-field-width"
                                            IsNumeric={true}
                                            onChange={value => this.mutateState(s => {
                                                s.vitalSign.bodyMassIndex = this.parseRawFloat(value);
                                                if (this.percentileUtility) {
                                                    s.percentiles["MassBodyIndex"] = this.percentileUtility?.calculatePercentile("MassBodyIndex", s.vitalSign);
                                                }
                                                s.saveLock = false;
                                            })}
                                            value={this.state.vitalSign?.bodyMassIndex?.toString() ?? ""}
                                        />
                                        <span className="units" />
                                        {this.renderPercentile("MassBodyIndex")}
                                    </div>
                                )}
                                {this.getAgeDifferenceFromDateOfBirthAndVitalSignPharmaStartDate() < 5
                                    ? this.renderHeadCircumferenceField()
                                    : this.renderBloodPressureField()
                                }
                                {this.state.showExtraFields && (
                                    <div>
                                        {this.renderRow(
                                            "PainScaleLabel",
                                            <PainScaleComponent
                                                changeScaleNumber={value => this.mutateState(s => {
                                                    s.vitalSign.painScaleValue = value;
                                                    s.saveLock = false;
                                                })}
                                                selectedScaleNumber={vitalSign.painScaleValue}
                                            />
                                        )}
                                        {this.renderRow(
                                            "TemperatureLabel",
                                            <div className="flex-inline-spacing">
                                                <TextboxControl
                                                    className="form-control text-field-width"
                                                    IsNumeric={true}
                                                    onChange={value => this.mutateState(s => {
                                                        s.vitalSign.temperature = this.parseRawFloat(value);
                                                        s.saveLock = false;
                                                    })}
                                                    value={this.state.vitalSign.temperature?.toString() ?? ""}
                                                />
                                                {this.renderTemperatureUnitSelect()}
                                                {this.renderTemperatureLocationSelect()}
                                            </div>
                                        )}
                                        {this.getAgeDifferenceFromDateOfBirthAndVitalSignPharmaStartDate() < 5 && this.renderBloodPressureField()}
                                        {this.renderRow(
                                            "HRLabel",
                                            <div className="flex-inline-spacing">
                                                <TextboxControl
                                                    className="form-control text-field-width"
                                                    IsNumeric={true}
                                                    onChange={value => this.mutateState(s => {
                                                        s.vitalSign.heartRate = this.parseRawFloat(value);
                                                        s.saveLock = false;
                                                    })}
                                                    value={this.state.vitalSign.heartRate?.toString() ?? ""}
                                                />
                                                <span>{cacheStore.GetString("BeatsPerMinuteAbbrevLabel")}</span>
                                                <InlineOptionsListControl
                                                    id="vital-sign-heart-rate-rythm"
                                                    value={this.state.vitalSign.heartRateRythm}
                                                    items={[
                                                        new InlineOption(HeartRateRythm.Regular, cacheStore.GetString("RegularLabel")),
                                                        new InlineOption(HeartRateRythm.Irregular, cacheStore.GetString("IrregularLabel"))
                                                    ]}
                                                    allowDeselect
                                                    onChange={option => this.mutateState(s => {
                                                        s.vitalSign.heartRateRythm = option?.Key ?? HeartRateRythm.NoneSelected;
                                                        s.saveLock = false;
                                                    })}
                                                />
                                            </div>
                                        )}
                                        {this.renderRow(
                                            <span>O<sub>2</sub>Sat</span>,
                                            <div className="flex-inline-spacing">
                                                <TextboxControl
                                                    className="form-control text-field-width"
                                                    IsNumeric={true}
                                                    onChange={value => this.mutateState(s => {
                                                        s.vitalSign.oxygenSaturation = this.parseRawFloat(value);
                                                        s.saveLock = false;
                                                    })}
                                                    value={this.state.vitalSign.oxygenSaturation?.toString() ?? ""}
                                                />
                                                <span>%</span>
                                            </div>
                                        )}
                                        {this.renderRow(
                                            "RespirationRateAbbrevLabel",
                                            <div className="flex-inline-spacing">
                                                <TextboxControl
                                                    className="form-control text-field-width"
                                                    IsNumeric={true}
                                                    onChange={value => this.mutateState(s => {
                                                        s.vitalSign.respirationRate = this.parseRawFloat(value);
                                                        s.saveLock = false;
                                                    })}
                                                    value={this.state.vitalSign.respirationRate?.toString() ?? ""}
                                                />
                                                <span>{cacheStore.GetString("PerMinuteAbbrevLabel")}</span>
                                            </div>
                                        )}
                                        {this.getAgeDifferenceFromDateOfBirthAndVitalSignPharmaStartDate() >= 5 && this.renderHeadCircumferenceField()}
                                        {this.renderRow(
                                            "WaistCircumferenceLabel",
                                            <div className="flex-inline-spacing">
                                                {this.renderDistanceField(
                                                    "vital-sign-popup-waist-circumference-units",
                                                    vitalSign.waistCircumference,
                                                    value => this.mutateState(s => {
                                                        value = s.vitalSign.waistCircumference = value;
                                                        s.saveLock = false;
                                                    }),
                                                    vitalSign.waistCircumferenceUnits,
                                                    value => this.mutateState(s => {
                                                        s.vitalSign.waistCircumferenceUnits = value;
                                                        s.saveLock = false;
                                                    })
                                                )}
                                            </div>
                                        )}
                                        {this.renderRow(
                                            "CreatinineClearanceAlgorithmLabel",
                                            this.renderCreatinineClearanceAlgorithmOptions()
                                        )}
                                        {this.renderRow(
                                            "SerumCreatinineLabel",
                                            <div className="flex-inline-spacing">
                                                <TextboxControl
                                                    className="form-control text-field-width"
                                                    IsNumeric={true}
                                                    onChange={value => this.mutateState(s => {
                                                        s.vitalSign.serumCreatinine = this.parseRawFloat(value);
                                                        s.saveLock = false;
                                                    })}
                                                    value={this.state.vitalSign.serumCreatinine?.toString() ?? ""}
                                                />
                                                {this.renderSerumCreatinineUnitSelect()}
                                            </div>
                                        )}
                                        {this.renderRow(
                                            "CreatinineClearanceLabel",
                                            this.renderCreatinineClearance()
                                        )}
                                        {this.renderRow(
                                            "BloodGlucoseLabel",
                                            <div className="flex-inline-spacing">
                                                <TextboxControl
                                                    className="form-control text-field-width"
                                                    IsNumeric={true}
                                                    onChange={value => this.mutateState(s => {
                                                        s.vitalSign.bloodGlucose = this.parseRawFloat(value);
                                                        s.saveLock = false;
                                                    })}
                                                    value={this.state.vitalSign.bloodGlucose?.toString() ?? ""}
                                                />
                                                {this.renderBloodGlucoseUnitSelect()}
                                            </div>
                                        )}
                                        {this.renderRow(
                                            "BPStandingLabel",
                                            <BloodPressureField
                                                cssClassNameInput="text-field-width"
                                                cssClassName="flex-inline-spacing"
                                                bloodPressure={this.state.vitalSign.bloodPressureStanding}
                                                onBloodPressureChange={bloodPressure => this.mutateState(s => {
                                                    s.vitalSign.bloodPressureStanding = bloodPressure;
                                                    s.saveLock = false;
                                                })}
                                            />
                                        )}
                                        {this.renderRow(
                                            "BPLayingLabel",
                                            <BloodPressureField
                                                cssClassNameInput="text-field-width"
                                                cssClassName="flex-inline-spacing"
                                                bloodPressure={this.state.vitalSign.bloodPressureLying}
                                                onBloodPressureChange={bloodPressure => this.mutateState(s => {
                                                    s.vitalSign.bloodPressureLying = bloodPressure;
                                                    s.saveLock = false;
                                                })}
                                            />
                                        )}
                                        {this.renderRow(
                                            "BodySurfaceDLabel",
                                            <TextboxControl
                                                className="form-control text-field-width"
                                                IsNumeric={true}
                                                onChange={value => this.mutateState(s => {
                                                    s.vitalSign.bodySurface = this.parseRawFloat(value);
                                                    s.saveLock = false;
                                                })}
                                                value={this.state.vitalSign.bodySurface?.toString() ?? ""}
                                            />
                                        )}
                                        {this.renderRow(
                                            "NoteDLabel",
                                            <TextAreaResizable
                                                id="vital-sign-popup-note-field"
                                                style={{ height: "100px" }}
                                                minHeightPx={100}
                                                text={vitalSign.note}
                                                isDisabled={false}
                                                onChange={value =>
                                                    this.mutateState(s => {
                                                        s.vitalSign.note = value;
                                                        s.saveLock = false;
                                                    })
                                                }
                                                verticalResizeOnly={true}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="margin-md-left margin-md-right">
                            <a onClick={() => this.mutateState(s => s.showExtraFields = !s.showExtraFields)}>
                                {cacheStore.GetString(this.state.showExtraFields ? "SeeLessLabel" : "SeeMoreLabel")}
                            </a>
                        </div>
                    </div>
                    {this.renderFooter()}
                </div>
            </MultipleComponentPopup>
        );
    }

    private getTitle(): string {
        return cacheStore.GetString(
            this.state.vitalSignToEdit
                ? this.getEditLabel()
                : this.getAddNewLabel()
        );
    }

    private getEditLabel(): string {
        return this.props.isAddingToExistingEncounter
            ? "EditVitalSignsLabel"
            : "EditVitalSignsInNewNoteLabel";
    }

    private getAddNewLabel(): string {
        return this.props.isAddingToExistingEncounter
            ? "AddVitalSignsLabel"
            : "AddVitalSignsInNewNoteLabel";
    }

    private renderPercentile(property: VitalProperty): React.ReactNode {
        const percentile = this.percentileUtility?.getPercentileText(property, this.state.percentiles);
        return percentile && <span style={{ fontStyle: "italic" }}>{percentile}</span>;
    }

    private renderRow(labelKey: string | JSX.Element, element: JSX.Element, locale: Locale = null): JSX.Element {
        return (
            <div className="row margin-md-top vital-sign-popup-row">
                <div className="col-md-9">
                    <label className="bold">{typeof labelKey === "string" ? cacheStore.GetString(labelKey, {}, locale) : labelKey}</label>
                    {element}
                </div>
            </div>
        );
    }

    private renderHeadCircumferenceField(): JSX.Element {
        return this.renderRow(
            "HeadCircumferenceLabel",
            this.renderDistanceField(
                "vital-sign-head-circumference",
                this.state.vitalSign.headCircumference,
                value => this.mutateState(s => {
                    s.vitalSign.headCircumference = value;
                    if (this.percentileUtility) {
                        s.percentiles["HeadCircumference"] = this.percentileUtility.calculatePercentile("HeadCircumference", s.vitalSign);
                    }

                    s.saveLock = false;
                }),
                this.state.vitalSign.headCircumferenceUnits,
                value => this.mutateState(s => {
                    s.vitalSign.headCircumferenceUnits = value;
                    s.saveLock = false;
                }),
                this.renderPercentile("HeadCircumference")
            )
        );
    }

    private renderBloodPressureField(): JSX.Element {
        return this.renderRow(
            "BPSittingLabel",
            <BloodPressureField
                cssClassNameInput="text-field-width"
                cssClassName="flex-inline-spacing"
                bloodPressure={this.state.vitalSign.bloodPressure}
                onBloodPressureChange={bloodPressure => this.mutateState(s => {
                    s.vitalSign.bloodPressure = bloodPressure;
                    s.saveLock = false;
                })}
            />
        );
    }

    private renderCreatinineClearanceAlgorithmOptions(): JSX.Element {
        const options = [
            { value: "", display: "" },
            ...cacheStore.FindLookupsByTypeCode(CreatinineClearanceAlgorithmLookup.Code)
                .map(p => ({ value: p.LookupID, display: p.GetValue() }))
        ];

        return (
            <select
                className="form-control text-field-width"
                value={this.state.vitalSign.creatineClearanceAlgorithmLookupId?.toString() ?? ""}
                onChange={(e: any) => {
                    const value = e.target?.value;
                    this.mutateState(s => {
                        s.vitalSign.creatineClearanceAlgorithmLookupId = value;
                        s.saveLock = false;
                    });
                }}
            >
                {options.map(p => <option key={`vital-sign-creatinine-algorithm-${p.value}`} value={p.value}>{p.display}</option>)}
            </select>
        );
    }

    private renderCreatinineClearance(): JSX.Element {
        const patientData = this.props.patientData;

        const creatineClearanceEnabled = patientData?.gender !== Gender.Unspecified && !!patientData?.dateOfBirth;
        const creatinineClearance = MedicalCalculator.calculateCreatinineClearance(patientData?.gender, patientData?.dateOfBirth, this.state.vitalSign);

        if (!creatineClearanceEnabled) {
            return (
                <div>
                    <span className="red-text"> {cacheStore.GetString("ClearanceMessage")} </span>
                </div>
            );
        }

        return creatinineClearance !== null
            ? <div>{creatinineClearance} mL/min/1.73m<sup>2</sup></div>
            : <div>{" -- "}</div>;
    }

    private renderWeight(): JSX.Element {
        const vsWeightId = "vital-sign-weight";
        const options = [
            { value: WeightUnit.Kilogram.toString(), display: cacheStore.GetString("KilogramUnitSymbolLabel") },
            { value: WeightUnit.Pound.toString(), display: cacheStore.GetString("PoundUnitSymbolLabel") }
        ];

        return (
            <div className="flex-inline-spacing">
                <TextboxControl
                    dataCy={`growthCharts-${vsWeightId}`}
                    id={vsWeightId}
                    className="form-control text-field-width"
                    IsNumeric={true}
                    onChange={value => this.mutateState(s => {
                        s.vitalSign.weight = this.parseRawFloat(value);
                        if (this.percentileUtility) {
                            s.percentiles["Weight"] = this.percentileUtility.calculatePercentile("Weight", s.vitalSign);
                            s.percentiles["MassBodyIndex"] = this.percentileUtility.calculatePercentile("MassBodyIndex", s.vitalSign);
                        }
                        s.saveLock = false;
                    })}
                    value={this.state.vitalSign.weight?.toString() ?? ""}
                />
                <select
                    data-cy={`growthCharts-${vsWeightId}Units`}
                    id={`${vsWeightId}-units`}
                    className="form-control units"
                    value={this.state.vitalSign.weightUnit?.toString() ?? ""}
                    onChange={(e: any) => {
                        const value = parseInt(e.target?.value) as WeightUnit;
                        this.mutateState(s => {
                            s.vitalSign.weightUnit = value;
                            s.saveLock = false;
                        });
                    }}
                >
                    {options.map(p => <option key={`vital-sign-weight-unit-${p.value}`} value={p.value}>{p.display}</option>)}
                </select>
                {this.renderPercentile("Weight")}
            </div>
        );
    }

    private renderDistanceField(
        id: string,
        value: number,
        onValueChange: (value: number) => void,
        unit: HeightUnit,
        onUnitChange: (unit: HeightUnit) => void,
        extraElement: React.ReactNode = null
    ): JSX.Element {
        const options = [
            { value: HeightUnit.Centimeter.toString(), display: cacheStore.GetString("CentimeterUnitSymbolLabel") },
            { value: HeightUnit.Inch.toString(), display: cacheStore.GetString("InchUnitSymbolLabel") }
        ];

        let unitId = `${id}-units`;

        return (
            <div className="flex-inline-spacing">
                <TextboxControl
                    dataCy={`growthCharts-${id}`}
                    id={id}
                    className="form-control text-field-width"
                    IsNumeric={true}
                    onChange={value => onValueChange(this.parseRawFloat(value))}
                    value={value?.toString() ?? ""}
                />
                <select
                    data-cy={`growthCharts-${id}Units`}
                    id={unitId}
                    className="form-control units"
                    value={unit?.toString() ?? ""}
                    onChange={(e: any) => onUnitChange(parseInt(e.target?.value) as HeightUnit)}
                >
                    {options.map(p => <option key={`${id}-${p.value}`} value={p.value}>{p.display}</option>)}
                </select>
                {extraElement}
            </div>
        );
    }

    private renderTemperatureUnitSelect(): JSX.Element {
        const options = [
            { value: TemperatureUnit.Celsius.toString(), display: cacheStore.GetString("CelsiusUnitSymbolLabel") },
            { value: TemperatureUnit.Fahrenheit.toString(), display: cacheStore.GetString("FahrenheitUnitSymbolLabel") }
        ];

        return (
            <select
                className="form-control units"
                value={this.state.vitalSign.temperatureUnit?.toString() ?? ""}
                onChange={(e: any) => {
                    const value = parseInt(e.target?.value) as TemperatureUnit;
                    this.mutateState(s => {
                        s.vitalSign.temperatureUnit = value;
                        s.saveLock = false;
                    });
                }}
            >
                {options.map(p => <option key={`vital-sign-temperature-${p.value}`} value={p.value}>{p.display}</option>)}
            </select>
        );
    }

    private renderSerumCreatinineUnitSelect(): JSX.Element {
        const options = [
            { value: SerumCreatinineConcentrationUnit.micromolesPerLitre.toString(), display: cacheStore.GetString("MicromolesPerLitreUnitSymbolLabel") },
            { value: SerumCreatinineConcentrationUnit.milligramsPer100millilitre.toString(), display: cacheStore.GetString("MilligramsPer100MillitresUnitSymbolLabel") }
        ];

        return (
            <select
                className="form-control units"
                value={this.state.vitalSign.serumCreatinineConcentrationUnit?.toString() ?? ""}
                onChange={(e: any) => {
                    const value = parseInt(e.target?.value) as SerumCreatinineConcentrationUnit;
                    this.mutateState(s => {
                        s.vitalSign.serumCreatinineConcentrationUnit = value;
                        s.saveLock = false;
                    });
                }}
            >
                {options.map(p => <option key={`vital-sign-serum-creatinine-unit-${p.value}`} value={p.value}>{p.display}</option>)}
            </select>
        );
    }

    private renderBloodGlucoseUnitSelect(): JSX.Element {
        const options = [
            { value: BloodGlucoseConcentrationUnit.millimolesPerLitre.toString(), display: cacheStore.GetString("MillimolesPerLitreUnitSymbolLabel") },
            { value: BloodGlucoseConcentrationUnit.milligramPerDecilitre.toString(), display: cacheStore.GetString("MilligramsPerDecaLitreUnitSymbolLabel") }
        ];

        return (
            <select
                className="form-control units"
                value={this.state.vitalSign.bloodGlucoseUnits?.toString() ?? ""}
                onChange={(e: any) => {
                    const value = parseInt(e.target?.value) as BloodGlucoseConcentrationUnit;
                    this.mutateState(s => {
                        s.vitalSign.bloodGlucoseUnits = value;
                        s.saveLock = false;
                    });
                }}
            >
                {options.map(p => <option key={`vital-sign-blood-glucose-unit-${p.value}`} value={p.value}>{p.display}</option>)}
            </select>
        );
    }

    private renderTemperatureLocationSelect(): JSX.Element {
        const options = cacheStore.FindLookupsByTypeName(TemperatureLocationsLookup.Code)
            .sortByPropertyDesc(x => x.LookupPosition)
            .map(p => ({ value: p.LookupID, display: p.GetValue() }));

        return (
            <select
                className="form-control"
                value={this.state.vitalSign.temperatureLocationLookupId?.toString() ?? ""}
                onChange={(e: any) => {
                    const value = e.target.value;
                    this.mutateState(s => {
                        s.vitalSign.temperatureLocationLookupId = value;
                        s.saveLock = false;
                    });
                }}
            >
                {options.map(p => <option key={"temperature-location-id" + p.value} value={p.value}>{p.display}</option>)}
            </select>
        );
    }

    private renderFooter(): JSX.Element {
        const disableSave = !this.state.vitalSign.date || this.state.saveLock;
        return (
            <div className="p-footer">
                <div className="footer-button-container">
                    <ButtonControl
                        className="btn btn-default"
                        onClick={() => this.props.onClose()}
                        label={this.getString("CancelLabel")}
                    />
                    {this.isVitalSignDeletable() && (
                        <ButtonControl
                            dataCy="growthCharts-vitalSign-delete"
                            id="delete-vital-signs-btn"
                            className="btn btn-danger"
                            onClick={() => this.deleteVitalSign()}
                            label={this.getString("DeleteLabel")}
                        />
                    )}
                    {!this.isVitalSignDeletable() && this.state.vitalSignToEdit && (
                        <ButtonControl
                            className="btn btn-danger"
                            label={cacheStore.GetString("InvalidateLabel")}
                            onClick={() => this.invalidateOrDeleteVitalSign(ServerOperations.Server.Myle_Patient_InvalidateVitalSign)}
                        />
                    )}
                    <ButtonControl
                        dataCy="growthCharts-vitalSign-save"
                        id="save-vital-signs-btn"
                        className="btn btn-primary"
                        disabled={disableSave}
                        label={this.getString("SaveLabel")}
                        onClick={() => this.saveLock()}
                    />
                    {(!this.props.disabledSavePlus) && (
                        <ButtonControl
                            className="btn btn-primary"
                            disabled={disableSave}
                            label={cacheStore.GetString("SaveLabel") + "[+]"}
                            onClick={() => this.saveLock(true)}
                        />
                    )}
                </div>
            </div>
        );
    }

    /**
     * Parse a string representation of a float number.
     * @param value The value to parse.
     */
    private parseRawFloat(value: string): number | null {
        const parsed = Number.parseFloat(value);
        return !Number.isNaN(parsed)
            ? parsed
            : null;
    }

    private getAgeDifferenceFromDateOfBirthAndVitalSignPharmaStartDate(): number {
        return LuxonDateHelper.dateDiff(this.props.patientData?.dateOfBirth, this.state.vitalSign.date, "years" as DurationUnit);
    }

    private saveLock(plus: boolean = false): void {
        this.mutateState(s => s.saveLock = true, () => this.onSave(plus));
    }

    private async onSave(plus: boolean = false): Promise<void> {
        const vitalSign = VitalSignModelConverter.convertToMedicalItem(
            this.state.vitalSign,
            this.state.vitalSignToEdit,
            this.props.patientData
        );
        vitalSign.EMRTemplateIsVisibleInCPP = true;

        const response = await VitalSignActions.save(
            vitalSign,
            this.state.reason,
            this.props.patientData.contactId,
            this.props.patientData.contactFacetId,
            this.state.currentSelectedSpecialty,
            cacheStore.GetCurrentEmployee().ActiveSiteId
        );
        if (response.IsNewNoteCreated) {
            sessionStore.dispatch(
                LocalPatientOperations.Profile.ShowRedActiveNoteTab,
                {
                    encounterId: response.RelatedEncounterId
                }
            );
        }
        patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(this.props.patientData.contactId, null, true, true, "VitalSignPopup_Save");
        if (plus)
            this.setState(s => new VitalSignPopupState({
                vitalSign: new VitalSign({
                    heightUnit: s.vitalSign.heightUnit,
                    weightUnit: s.vitalSign.weightUnit,
                    headCircumferenceUnits: s.vitalSign.headCircumferenceUnits,
                    waistCircumferenceUnits: s.vitalSign.waistCircumferenceUnits,
                    temperatureUnit: s.vitalSign.temperatureUnit,
                    serumCreatinineConcentrationUnit: s.vitalSign.serumCreatinineConcentrationUnit,
                    bloodGlucoseUnits: s.vitalSign.bloodGlucoseUnits,
                    date: DateTime.nowDate()
                }),
                reason: this.getReason(true),
                currentSelectedSpecialty: s.currentSelectedSpecialty,
                vitalSignToEdit: null,
                percentiles: this.percentileUtility?.getAllCalculatedPercentiles(new VitalSign()),
                saveLock: false
            }));
        else
            this.props.onClose(response.RelatedEncounterId, vitalSign);
    }

    private isVitalSignDeletable(): boolean {
        if (!this.state.vitalSignToEdit)
            return false;

        var patientEncounter = patientStore.PatientMedicalSummaryModels[patientStore.getCurrentPatientContactId()]
            ?.get("Encounters")
            ?.find(encounter => encounter.Id === this.state.vitalSignToEdit.RelatedEncounterId);

        if (patientEncounter?.IsSignedOff)
            return false;

        var emrTemplateVitalSigns = patientStore.PatientMedicalSummaryModels[patientStore.getCurrentPatientContactId()]
            ?.get("MedicalItemsVitalSign")
            ?.filter(vitalSign => vitalSign.EMRTemplateId === this.state.vitalSignToEdit.EMRTemplateId);

        if (emrTemplateVitalSigns.count() > 1)
            return false;

        return true;
    }

    private async invalidateOrDeleteVitalSign(serverOperationNumber: number): Promise<void> {
        await VitalSignActions.invalidateOrDelete(
            serverOperationNumber,
            this.state.vitalSignToEdit.MedicalItemId
        );

        patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(this.props.patientData.contactId, null, true, true, "VitalSignPopup_InvalidateOrDelete");
        this.props.onClose();
    }

    private getDefaultUnits(): VitalSignDefaultUnits {
        const defaultUnits: VitalSignDefaultUnits = {
            heightUnit: HeightUnit.Centimeter,
            weightUnit: WeightUnit.Kilogram,
            headCircumferenceUnits: HeightUnit.Centimeter,
            waistCircumferenceUnits: HeightUnit.Centimeter,
            temperatureUnit: TemperatureUnit.Celsius,
            serumCreatinineConcentrationUnit: SerumCreatinineConcentrationUnit.micromolesPerLitre,
            bloodGlucoseUnits: BloodGlucoseConcentrationUnit.millimolesPerLitre
        };
        if (!sessionStore.useMetricSystemAsDefault) {
            defaultUnits.heightUnit = HeightUnit.Inch;
            defaultUnits.weightUnit = WeightUnit.Pound;
            defaultUnits.headCircumferenceUnits = HeightUnit.Inch;
            defaultUnits.waistCircumferenceUnits = HeightUnit.Inch;
            defaultUnits.temperatureUnit = TemperatureUnit.Fahrenheit;
        }
        return defaultUnits;
    }

    private getReason(isNewVitalSign: boolean): string {
        if (this.props.vitalSignToEdit && !isNewVitalSign) {
            return this.props.vitalSignToEditReaon ?? "";
        }
        else {
            if (this.props.isAddingToExistingEncounter && !this.props.vitalSignToEdit && !!this.props.vitalSignToEditReaon)
                return this.props.vitalSignToEditReaon;

            return PatientHelper.isPatientChild(this.props.patientData?.dateOfBirth)
                ? this.getString("PediatricFollowUpLabel")
                : "";
        }
    }

    private deleteVitalSign(): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("RemoveForeverMessageSelected"),
            content: `${cacheStore.GetString("DeleteMedicalItem1Label")} ${cacheStore.GetString("DeleteAffectationMessage2Label")}`,
            showCancel: true,

            okLabel: cacheStore.GetString("OKLabel"),
            okAction: () => this.invalidateOrDeleteVitalSign(ServerOperations.Server.Myle_Patient_DeleteVitalSign)
        });
    }
}
